import * as Sentry from '@sentry/browser';

import { TextTrackTranslationNode } from 'src/generated/webapp_gql';
import { VideoJSTextTrack, VideoJsTextTrackKind } from 'src/shared/components/VideoPlayer';
import { DefaultSupportedLocaleDisplayName, DefaultSupportedLocaleKey } from '../models/shared';

/**
 * Narrows a maybe-defined translated textTrack and a definitely-defined textTrack
 * down to a single textTrack for use in displaying subtitles for VideoContent
 * @param videoContentID The ID for the VideoContent being rendered
 * @param textTrack A URL to the source language of the video, typically defaults to English
 * @param textTrackTranslation A language code, label, and URL
 * @returns A single, valid textTrack, or undefined.
 */
export function resolveTextTrack(
    videoContentID: string,
    textTrack?: string | null,
    textTrackTranslation?: Pick<
        TextTrackTranslationNode,
        'contentLanguage' | 'label' | 'textTrack'
    > | null,
): VideoJSTextTrack | null {
    if (textTrackTranslation && textTrackTranslation.contentLanguage) {
        return {
            kind: VideoJsTextTrackKind.Subtitles,
            label: textTrackTranslation.label,
            srcLang: textTrackTranslation.contentLanguage,
            src: textTrackTranslation.textTrack,
            default: shouldTextTrackDefaultOn(textTrackTranslation.contentLanguage),
        };
    } else if (textTrack) {
        return {
            kind: VideoJsTextTrackKind.Subtitles,
            label: DefaultSupportedLocaleDisplayName,
            srcLang: DefaultSupportedLocaleKey,
            src: textTrack,
            default: shouldTextTrackDefaultOn(DefaultSupportedLocaleKey),
        };
    } else {
        Sentry.withScope(scope => {
            scope.setExtras({
                videoContentID,
                textTrack,
                textTrackTranslation,
            });
            Sentry.captureException(
                new Error('Expected one of textTrack or textTrackTranslation for VideoContent'),
            );
        });
        return null;
    }
}

/**
 * A utility method for audiovisual content to determine whether or not subtitles should
 * default to being enabled.
 * @param contentLanguage The locale key for the user's preferred content language
 * @returns If true, the textTrackTranslation should be shown by default
 */
function shouldTextTrackDefaultOn(contentLanguage: string): boolean {
    return contentLanguage !== DefaultSupportedLocaleKey;
}
